import { DEFAULT_WEEKLY_CAPACITY } from 'constants/account';
import { EMPTY_ARRAY } from 'constants/defaults';

import { UNASSIGNED_TO_TEAM_ID } from 'constants/teams';
import type { TeamFilterDropdownOption, TeamUser, TeamWithUsers } from 'factories/teams';
import {
  teamDraftFactory,
  teamFilterOptionFactory,
  userOnTeamAssociationFactory,
} from 'factories/teams';
import { xhrStatusUIState } from 'redux/reusable/xhrStatus';
import { getCurrentAccount } from 'redux/selectors/accounts';

import { getNonDeletedUsers } from 'redux/selectors/users';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import { TEAM_SCHEMA_KEY } from 'schema/teams';
import type { Team } from 'types/team';
import type { User } from 'types/user';
import { naturalCompare } from 'utils/naturalSort';
import { isInteger } from 'utils/typesafe';
import { generateUserDraft } from 'utils/users';
import { getDefaultUserRoleId } from './roles';
import { getUserId } from './user';

export const NO_TEAM_ASSIGNMENT = 0;

export const getTeamEntities = (state: IState) => state.teams.entities[TEAM_SCHEMA_KEY];
export const getTeamResult = (state: IState) => state.teams.result;
const getDraft = (state: IState) => state.teams.draft;
const readXhrStatus = (state: IState) => state.teams.xhrStatus;
export const getXhrStatus = createSelector([readXhrStatus], xhrStatus =>
  xhrStatusUIState(xhrStatus)
);
const readShowTeamsManagementModal = (state: IState) => state.teams.showTeamsManagementModal;

export const getShowTeamsManagementModal = readShowTeamsManagementModal;

export const getTeamError = createSelector([getXhrStatus], xhrStatus => {
  return xhrStatus.hasErrors;
});

export type TeamAssociatedUser = TeamUser & {
  selected: boolean;
};
export const getUserRelationsToTeam = createSelector(
  [getDraft, getNonDeletedUsers],
  (draft, users) => {
    const draftUsers = draft?.users || EMPTY_ARRAY;
    return users.map((user): TeamAssociatedUser => {
      const draftUser = draftUsers.find(({ user_id }) => user_id === user.id);
      const isTeamLead = !!draftUser?.lead;
      return {
        ...draftUser,
        ...userOnTeamAssociationFactory(user, isTeamLead),
        selected: draftUser !== undefined,
      };
    });
  }
);
export const getTeamLeadRelationsToTeam = createSelector(
  [getDraft, getNonDeletedUsers],
  (draft, users) => {
    return users.map((user): TeamAssociatedUser => {
      const draftUsers = draft?.users || EMPTY_ARRAY;
      const selected = draftUsers.some(({ user_id, lead }) => user_id === user.id && lead);
      const isTeamLead = draftUsers.some(({ lead }) => !!lead);
      return {
        ...userOnTeamAssociationFactory(user, isTeamLead),
        selected,
      };
    });
  }
);

export const isTeamDraftSaveDisabled = createSelector(
  [getDraft, getXhrStatus],
  (draft, { isLoading, isSaving }) => {
    const hasName = !!(draft?.name && draft.name.length > 0);
    const hasMembers = draft?.users != null && draft.users.length > 0;
    return !!(!hasName || isLoading || isSaving || !hasMembers);
  }
);

export const getTeams = createSelector(
  [getTeamEntities, getTeamResult],
  (teams, teamIds): Array<Team> => {
    return teamIds.map(teamId => teams[teamId]).sort((a, b) => naturalCompare(a.name, b.name));
  }
);

export const getTeamsAsDropdownOptions = createSelector(
  [getTeams],
  (teams): Array<TeamFilterDropdownOption> => {
    const teamOptions: Array<TeamFilterDropdownOption> = teams.map(team =>
      teamFilterOptionFactory({
        id: team.id,
        name: team.name,
        emoji: team.emoji,
        deleted: team.deleted,
      })
    );

    if (!teams.length) {
      return EMPTY_ARRAY;
    }

    // TODO Access Levels
    // if (shouldNotSeeNoTeamAssignmentOption) {
    //   return teamOptions;
    // }

    const emptyOption: TeamFilterDropdownOption = teamFilterOptionFactory({
      sequence: -1,
      id: UNASSIGNED_TO_TEAM_ID,
      name: 'No team assignment',
      emoji: null,
    });

    return [emptyOption, ...teamOptions];
  }
);

export const getTeamsWithUsers = createSelector(
  [getTeamEntities, getTeamResult, getNonDeletedUsers, getDefaultUserRoleId, getCurrentAccount],
  (
    teams: {
      [teamId: number]: Team;
    },
    teamIds: Array<number>,
    users: Array<User>,
    defaultRoleId,
    account
  ): Array<TeamWithUsers> => {
    const workspaceWeeklyCapacity = account.weekly_user_capacity ?? DEFAULT_WEEKLY_CAPACITY;
    const defaultWeekdays = account.default_work_days;
    return teamIds.map((teamId: number): TeamWithUsers => {
      const { users: usersFromServer, ...team } = teams[teamId];
      const usersOnTeam: Array<TeamUser> = (usersFromServer || [])
        .map(({ user_id, lead }) => {
          const user =
            users.find(u => u.id === user_id) ||
            generateUserDraft(
              { id: user_id },
              { defaultRoleId, workspaceWeeklyCapacity, defaultWeekdays }
            );
          return userOnTeamAssociationFactory(
            {
              ...user,
              id: user.id!,
            },
            !!lead
          );
        })
        .sort((a, b) => naturalCompare(a.name, b.name));
      return {
        ...team,
        users: usersFromServer,
        teamUsers: usersOnTeam,
      };
    });
  }
);

export const getUserIdsByTeamId = createSelector([getTeams], teams => {
  return teams.reduce((map: Record<number, Array<number> | undefined>, team) => {
    map[team.id!] = (team.users ?? [])
      .filter(u => !(u.lead && u.hide_hours === true))
      .map(u => u.user_id);
    return map;
  }, {});
});

// Is this array loopup fine, or should be share a "massage the data" function in getTeams & here?
export const getTeamDraft = createSelector([getTeamsWithUsers, getDraft], (teams, draft) => {
  if (!draft) return null;
  // Return team, or a skeleton object that won't break views
  const team = teams.find(t => t.id === draft.id) || teamDraftFactory({ id: draft.id, users: [] });

  return {
    ...team,
    ...draft,
  };
});

export const hasValidTeamsDraft = createSelector([getDraft], draft => {
  return !!(draft && Object.keys(draft).length > 0);
});

export const getHasReachedTeamsLimit = createSelector(
  [getCurrentAccount, getTeamResult],
  (account, teams) => {
    if (!isInteger(account.features.teams) || account.features.teams === -1) {
      return false;
    }
    return account.features.teams <= teams.length;
  }
);

/**
 * Gets a set containing the IDs of users belonging to teams that the current user is a lead of.
 */
export const getUserLedUserIds = createSelector([getUserId, getTeams], (currentUserId, teams) => {
  const userIds = new Set<number>();
  for (const team of teams) {
    if (!team.id) continue;

    if (team.users.some(u => u.user_id === currentUserId && u.lead)) {
      for (const user of team.users) {
        userIds.add(user.user_id);
      }
    }
  }
  return userIds;
});

export const getUserIdsFromTeams = createSelector(
  [getNonDeletedUsers, getUserIdsByTeamId, (_, teamIds) => teamIds],
  (nonDeletedUsers, userIdsByTeamId, teamIds) => {
    if (teamIds.length === 0) return [];
    const userIds = nonDeletedUsers.map(u => u.id);

    const resultUserIds: Array<Array<number>> = [];
    teamIds.forEach((teamId: number) => {
      let ids;
      if (teamId === NO_TEAM_ASSIGNMENT) {
        const usersInTeams = Object.keys(userIdsByTeamId).flatMap(t => userIdsByTeamId[Number(t)]);
        const uniqUsersInTeams = new Set(usersInTeams);
        ids = userIds.filter(id => !uniqUsersInTeams.has(id));
      } else {
        ids = userIdsByTeamId[teamId]!;
      }
      resultUserIds.push(ids);
    });

    return resultUserIds.flat();
  }
);

export const getIsUserTeamLead = createSelector([getUserId, getTeamsWithUsers], (userId, teams) => {
  return teams.some(team => {
    return team.users.some(user => user.user_id === userId && user.lead);
  });
});
